// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css'; // if you have global CSS

// Detect iOS device
const isIOS = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

// Detect development environment
const isDevelopmentEnvironment = () => {
  // For production builds, always return false regardless of hostname
  // This ensures production deployments never use development settings
  if (process.env.NODE_ENV === 'production') {
    return false;
  }
  
  // Only consider it development if explicitly set to development
  // or running on a local development server
  return (
    process.env.NODE_ENV === 'development' || 
    window.location.hostname === 'localhost'
  );
};

// Flag the environment in localStorage for other components
const isDevEnvironment = isDevelopmentEnvironment();
localStorage.setItem('app_environment', isDevEnvironment ? 'development' : 'production');
console.log(`Environment detected: ${isDevEnvironment ? 'development' : 'production'}`);

if (isDevEnvironment) {
  
  // After DOM is loaded, add visual indicators for development version
  window.addEventListener('DOMContentLoaded', () => {
    // Mark the document for development - helps iOS distinguish the app
    document.documentElement.setAttribute('data-environment', 'development');
    
    if (document.title.indexOf('DEV') === -1) {
      document.title = 'DEV-' + document.title;
    }
    
    // Create a development badge for visual distinction
    const devBadge = document.createElement('div');
    devBadge.id = 'dev-environment-badge';
    devBadge.style.position = 'fixed';
    devBadge.style.top = '60px';  // Moved down to band below main menu
    devBadge.style.left = '20px'; // Positioned on left side as requested
    devBadge.style.backgroundColor = 'red';
    devBadge.style.color = 'white';
    devBadge.style.padding = '5px 10px';
    devBadge.style.borderRadius = '5px';
    devBadge.style.fontWeight = 'bold';
    devBadge.style.zIndex = '9999';
    devBadge.style.fontSize = '12px';
    devBadge.textContent = 'DEV';
    
    document.body.appendChild(devBadge);
    
    // For iOS, add extra distinction for PWA
    if (isIOS()) {
      // Create unique localStorage entries to help iOS distinguish this app
      localStorage.setItem('app_id', 'com.zoomingroomin.portal.dev');
      localStorage.setItem('app_version', 'dev-' + Date.now());
      
      // Add iOS-specific meta tag via JavaScript
      const metaTag = document.createElement('meta');
      metaTag.name = 'apple-mobile-web-app-title';
      metaTag.content = 'DEV-ZG-Portal';
      document.head.appendChild(metaTag);
    }
  });
}

// Modified service worker registration to be more iOS-friendly and development-aware
const registerServiceWorker = async () => {
  // Add development environment marker to service worker registration
  const isDev = isDevelopmentEnvironment();
  // Only proceed if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker is not supported in this browser');
    return;
  }

  // Special handling for iOS devices to prevent refresh loops
  const isIOSDevice = isIOS();
  if (isIOSDevice) {
    console.log('iOS device detected - using simplified service worker registration');
    
    try {
      // For iOS, only register if no service worker exists
      const existingRegistrations = await navigator.serviceWorker.getRegistrations();
      if (existingRegistrations.length > 0) {
        console.log('Service worker already registered on iOS, skipping registration');
        return existingRegistrations[0]; // Return existing registration
      }
      
      // If no service worker exists, register without the aggressive cleanup
      const registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
      console.log('Service Worker registered on iOS with scope:', registration.scope);
      return registration;
    } catch (error) {
      console.error('Error registering service worker on iOS:', error);
      return null;
    }
  }

  // Standard registration for non-iOS devices
  try {
    // First, unregister any existing service workers for a clean start
    const registrations = await navigator.serviceWorker.getRegistrations();
    console.log(`Found ${registrations.length} service worker registrations to remove`);
    
    for (let registration of registrations) {
      console.log('Unregistering service worker:', registration.scope);
      const result = await registration.unregister();
      console.log('Unregister result:', result);
    }
    
    // Clear all caches to start fresh
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      console.log(`Found ${cacheNames.length} caches to delete`);
      
      await Promise.all(
        cacheNames.map(cacheName => {
          console.log('Deleting cache:', cacheName);
          return caches.delete(cacheName);
        })
      );
    }
    
    console.log('Successfully unregistered all service workers and cleared caches.');
    
    // Register our new service worker with redirect handling
    // Add cache-busting query parameter to ensure we get the latest version
    const timestamp = new Date().getTime();
    const registration = await navigator.serviceWorker.register(
      `/service-worker.js?v=${timestamp}`, 
      { scope: '/' }
    );
    
    console.log('Service Worker registered with scope:', registration.scope);
    
    // Handle service worker updates - REMOVED FORCED PAGE RELOAD
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      console.log('New service worker installing:', newWorker);
      
      newWorker.addEventListener('statechange', () => {
        console.log('Service worker state changed to:', newWorker.state);
        // Removed automatic reload to prevent refresh loops
      });
    });
    
    // Handle controller change events (when a new service worker takes control)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service worker controller changed');
    });
    
    return registration;
  } catch (error) {
    console.error('Error registering service worker:', error);
    return null;
  }
};

// Call register function when page loads
window.addEventListener('load', registerServiceWorker);

/* 
 * Service worker registration has been modified to be more iOS-friendly:
 * - Special handling for iOS devices to prevent refresh loops
 * - Removed forced page reload on service worker activation
 * - Simplified registration process for iOS devices
 */

const container = document.getElementById('root');
if (!container) {
  throw new Error("No element with id 'root' found in public/index.html");
}
const root = ReactDOM.createRoot(container);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
